import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin, CheckCircle2, Wand2, Search, AlertCircle, Pencil, X, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available leaderboard columns the manager can map a custom field to.
// `__none` is the sentinel for "no mapping" (treat as a true custom field
// — only shows on the leaderboard if pinned via Custom Columns).
const CANONICAL_OPTIONS = [
  { value: "__none", label: "Custom (no mapping)" },
  { value: "dials",        label: "Dials" },
  { value: "connects",     label: "Connects" },
  { value: "dms",          label: "DMs" },
  { value: "sets",         label: "Sets" },
  { value: "shows",        label: "Shows" },
  { value: "no_shows",     label: "No-Shows" },
  { value: "followups",    label: "Follow-ups" },
  { value: "deals",        label: "Deals" },
  { value: "cash",         label: "Cash" },
  { value: "inbound_ctas", label: "Inbound CTAs" },
  { value: "dqs",          label: "DQs" },
];

const SOURCE_BADGE = {
  override: { color: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300", label: "Manual" },
  auto:     { color: "bg-blue-500/10 border-blue-500/30 text-blue-300",            label: "Auto" },
  none:     { color: "bg-white/5 border-white/10 text-gray-500",                   label: "Unmapped" },
};

/**
 * Field Mapping admin tool — lets a manager point ANY EOD custom field
 * key (q_xxx, "Cold Outbound Calls", "Instagram DMs") at a canonical
 * leaderboard column.
 *
 * Surfaces every distinct custom field seen on activity_logs in the last
 * N days, annotated with:
 *   - Human label (resolved from EOD template custom_questions[].label)
 *   - Sample numeric value (first non-null we saw)
 *   - Currently-mapped canonical column (manual override / auto-aliased / unmapped)
 *   - Source badge: Manual / Auto / Unmapped
 *
 * Saving the mapping immediately updates the leaderboard on the next
 * load — no migration needed since aliasing is read-time.
 *
 * Iter154 — `focusField` deep-links to a specific row. When set, after
 * the dialog loads its data we:
 *   1. Scroll the matching row into view.
 *   2. Flash an emerald ring on it for 2s so it's visually unmistakable.
 *   3. Auto-open the inline label editor when the field has no label
 *      yet — the most common reason a manager arrives here is to give
 *      a cryptic q_XXXXX a friendly name.
 */
export function FieldMappingDialog({ open, onOpenChange, focusField = null }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [discovered, setDiscovered] = useState([]);
  const [mappings, setMappings] = useState({});       // raw_key -> canonical
  const [pendingChanges, setPendingChanges] = useState({}); // raw_key -> canonical (drafts)
  const [search, setSearch] = useState("");

  // Iter153 — inline label editing state. {fieldKey: draftLabel | undefined}
  // Undefined = not currently editing this row.
  const [labelDrafts, setLabelDrafts] = useState({});
  const [labelSaving, setLabelSaving] = useState(null);

  // Iter154 — refs for deep-link focus + flash highlight.
  const rowRefs = useRef({});
  const [flashKey, setFlashKey] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPendingChanges({});
    setSearch("");
    setLabelDrafts({});
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/eod/field-mappings?days=180`, { credentials: "include" });
        if (res.ok) {
          const d = await res.json();
          setDiscovered(d.discovered || []);
          setMappings(d.mappings || {});
        } else if (res.status === 403) {
          toast.error("Only managers / admins can edit field mappings.");
          onOpenChange(false);
        }
      } catch (e) {
        console.error("Failed to load field mappings:", e);
        toast.error("Couldn't load field mappings.");
      } finally { setLoading(false); }
    })();
  }, [open, onOpenChange]);

  // Iter154 — once the discovered list lands, scroll the focused row
  // into view, flash it, and auto-open the inline label editor if it
  // doesn't have a label yet. Runs *after* discovered hydrates so the
  // ref exists in the DOM. Cleared after first apply so the user can
  // scroll freely without us re-snapping them.
  const focusHandled = useRef(false);
  useEffect(() => {
    if (!open) {
      focusHandled.current = false;
      setFlashKey(null);
      return;
    }
    if (focusHandled.current) return;
    if (loading || !focusField) return;
    const row = rowRefs.current[focusField];
    if (!row) return;
    focusHandled.current = true;
    setSearch("");
    try {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch { /* old browsers */ }
    setFlashKey(focusField);
    // Auto-open editor only if the row currently has no resolved label
    // — that's the highest-intent action when a manager arrives here.
    const target = discovered.find((f) => f.key === focusField);
    if (target && (!target.label || target.label === target.key)) {
      setLabelDrafts((p) => ({ ...p, [focusField]: "" }));
    }
    const t = setTimeout(() => setFlashKey(null), 2000);
    return () => clearTimeout(t);
  }, [open, loading, focusField, discovered]);

  const effectiveMapping = (field) => {
    // pendingChanges wins over committed mappings wins over auto-resolution
    if (field.key in pendingChanges) return pendingChanges[field.key];
    if (field.key in mappings) return mappings[field.key];
    return field.current_mapping || "__none";
  };

  const effectiveSource = (field) => {
    if (field.key in pendingChanges) return "override"; // pending edit
    if (field.key in mappings) return "override";
    return field.source;
  };

  const setMapping = (key, canonical) => {
    setPendingChanges((prev) => ({ ...prev, [key]: canonical }));
  };

  // Iter158 — one-click "Backfill labels from templates" so accounts
  // that existed before Iter153 can catch up their registry in one
  // shot instead of typing 29 labels by hand.
  const [backfilling, setBackfilling] = useState(false);
  // Iter159 — LLM-powered label suggestion state.
  const [suggesting, setSuggesting] = useState(false);
  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const r = await fetch(`${API}/eod/field-mappings/backfill-labels`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) {
        toast.error("Couldn't backfill labels.");
        return;
      }
      const data = await r.json();
      const added = data.labels_added || 0;
      const updated = data.labels_updated || 0;
      const total = added + updated;
      if (total === 0) {
        toast.info(
          `Scanned ${data.templates_scanned} template${data.templates_scanned === 1 ? "" : "s"} — no new labels to recover.`,
        );
      } else {
        toast.success(
          `Backfilled ${total} label${total === 1 ? "" : "s"} (${added} new, ${updated} updated).`,
        );
      }
      // Reload so the discovered list picks up the freshly-resolved labels.
      const refresh = await fetch(`${API}/eod/field-mappings?days=180`, { credentials: "include" });
      if (refresh.ok) {
        const d = await refresh.json();
        setDiscovered(d.discovered || []);
        setMappings(d.mappings || {});
      }
    } catch {
      toast.error("Network error during backfill.");
    } finally {
      setBackfilling(false);
    }
  };

  // Iter159 — Claude proposes a label for every unlabeled row in one
  // batch. Suggestions are AUTO-SAVED so the user sees them resolved
  // immediately — they can still edit any wrong one via the pencil.
  // Skipping the "review each row" step trades 29 keystrokes for one
  // click; the cost of fixing a mis-suggestion is identical to fixing
  // a mis-typed label, so net UX is clearly better.
  const runSuggestLabels = async () => {
    const targets = discovered
      .filter((f) => !f.label || f.label === f.key || f.label_source === "none")
      .map((f) => f.key);
    if (targets.length === 0) {
      toast.info("Nothing to suggest — every field already has a label.");
      return;
    }
    setSuggesting(true);
    try {
      const r = await fetch(`${API}/eod/field-mappings/suggest-labels`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ field_ids: targets }),
      });
      if (!r.ok) {
        toast.error("Couldn't fetch suggestions.");
        return;
      }
      const data = await r.json();
      const suggestions = (data.suggestions || []).filter(
        (s) => s.field_id && s.suggested_label,
      );
      if (suggestions.length === 0) {
        toast.info("Claude couldn't confidently propose labels.");
        return;
      }

      // Auto-save every suggestion in parallel via the existing label
      // endpoint. Each save persists to the registry with source="manual".
      const results = await Promise.allSettled(
        suggestions.map((s) =>
          fetch(`${API}/eod/field-mappings/label`, {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ field_id: s.field_id, label: s.suggested_label }),
          }).then((res) => (res.ok ? res.json() : Promise.reject(res.status))),
        ),
      );

      // Patch the local `discovered` list so the UI updates without a
      // full refetch.
      const labelByKey = new Map();
      results.forEach((r, i) => {
        if (r.status === "fulfilled") {
          labelByKey.set(suggestions[i].field_id, suggestions[i].suggested_label);
        }
      });
      setDiscovered((prev) =>
        prev.map((f) =>
          labelByKey.has(f.key)
            ? { ...f, label: labelByKey.get(f.key), label_source: "manual" }
            : f,
        ),
      );
      const saved = labelByKey.size;
      const failed = results.length - saved;
      if (saved && !failed) {
        toast.success(`Saved ${saved} AI label suggestion${saved === 1 ? "" : "s"}.`);
      } else if (saved && failed) {
        toast.warning(`Saved ${saved}, ${failed} failed — try Refresh.`);
      } else {
        toast.error("Couldn't save any suggestions.");
      }
    } catch {
      toast.error("Network error fetching suggestions.");
    } finally {
      setSuggesting(false);
    }
  };

  // Iter153 — manual label override. POSTs to /api/eod/field-mappings/label
  // and patches the local `discovered` row so the UI updates immediately
  // without re-fetching the whole list.
  const startEditLabel = (field) => {
    setLabelDrafts((prev) => ({ ...prev, [field.key]: field.label === field.key ? "" : field.label }));
  };
  const cancelEditLabel = (key) => {
    setLabelDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };
  const saveLabel = async (field) => {
    const draft = (labelDrafts[field.key] ?? "").trim();
    setLabelSaving(field.key);
    try {
      const res = await fetch(`${API}/eod/field-mappings/label`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ field_id: field.key, label: draft }),
      });
      if (!res.ok) {
        toast.error("Couldn't save label.");
        return;
      }
      const data = await res.json();
      setDiscovered((prev) =>
        prev.map((f) =>
          f.key === field.key
            ? { ...f, label: data.label || f.key, label_source: data.label_source || "none" }
            : f,
        ),
      );
      cancelEditLabel(field.key);
      toast.success(draft ? "Label saved" : "Label cleared");
    } catch {
      toast.error("Network error saving label.");
    } finally {
      setLabelSaving(null);
    }
  };
  const copyId = (key) => {
    try {
      navigator.clipboard?.writeText(key);
      toast.success(`Copied ${key}`);
    } catch {
      /* ignore */
    }
  };

  // Iter142 — Pull heuristic suggestions, stage them as pendingChanges so
  // the manager reviews/edits before saving. We don't auto-save: managers
  // want to eyeball the proposals (especially for value-shape guesses
  // like "sample $4,500 → Cash") before committing.
  const [autoSuggesting, setAutoSuggesting] = useState(false);
  const runAutoSuggest = async () => {
    setAutoSuggesting(true);
    try {
      const res = await fetch(`${API}/eod/field-mappings/auto-suggest`, { credentials: "include" });
      if (!res.ok) {
        toast.error("Couldn't auto-suggest mappings right now.");
        return;
      }
      const data = await res.json();
      const proposed = data.suggestions || [];
      if (proposed.length === 0) {
        toast.info("No new fields to suggest — everything is already mapped or unrecognizable.");
        return;
      }
      const next = { ...pendingChanges };
      for (const s of proposed) {
        if (s.suggested) next[s.key] = s.suggested;
      }
      setPendingChanges(next);
      toast.success(
        `Suggested ${proposed.length} mapping${proposed.length === 1 ? "" : "s"} — review and Save to apply.`
      );
    } catch (e) {
      toast.error("Network error while auto-suggesting.");
    } finally {
      setAutoSuggesting(false);
    }
  };

  const filtered = search
    ? discovered.filter((f) =>
        f.key.toLowerCase().includes(search.toLowerCase()) ||
        (f.label || "").toLowerCase().includes(search.toLowerCase()),
      )
    : discovered;

  const unmappedCount = discovered.filter((f) => effectiveSource(f) === "none").length;
  // Iter158 — fields whose human label couldn't be resolved (catalog
  // shows "Unknown label"). Drives the Backfill button visibility.
  const unknownLabelCount = discovered.filter(
    (f) => !f.label || f.label === f.key || f.label_source === "none",
  ).length;
  const dirtyCount = Object.keys(pendingChanges).length;

  const save = async () => {
    setSaving(true);
    try {
      // Build the full mappings dict — start with committed mappings, then
      // apply pending edits. Map __none to null/empty so the backend drops
      // the entry.
      const merged = { ...mappings };
      for (const [k, v] of Object.entries(pendingChanges)) {
        if (v === "__none" || !v) {
          delete merged[k];
        } else {
          merged[k] = v;
        }
      }
      const res = await fetch(`${API}/eod/field-mappings`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mappings: merged }),
      });
      if (res.ok) {
        const d = await res.json();
        setMappings(d.mappings || {});
        setDiscovered(d.discovered || []);
        setPendingChanges({});
        toast.success(`Saved ${dirtyCount} field mapping${dirtyCount === 1 ? "" : "s"}`);
        onOpenChange(false);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === "string" ? err.detail : "Couldn't save mappings.");
      }
    } catch {
      toast.error("Network error saving mappings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="field-mapping-dialog"
        className="bg-[#0F0F0F] border-white/10 text-white max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-emerald-400" />
            EOD Field Mapping
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Tell the leaderboard which canonical column each custom EOD field belongs to. New mappings apply on the next leaderboard load — no migration needed.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-gray-500">
            <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Discovering custom fields…
          </div>
        ) : discovered.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <Wand2 className="w-8 h-8 mx-auto mb-3 opacity-60" />
            <p className="text-sm">No custom fields detected in the last 180 days.</p>
            <p className="text-xs mt-1">As reps submit EODs with custom template fields, they&apos;ll appear here.</p>
          </div>
        ) : (
          <>
            {/* Summary bar */}
            <div className="flex items-center gap-3 px-1 py-2 text-xs text-gray-400">
              <span data-testid="field-mapping-summary">
                {discovered.length} custom field{discovered.length === 1 ? "" : "s"} detected
                {unmappedCount > 0 && (
                  <span className="ml-2 text-amber-300">
                    · <AlertCircle className="w-3 h-3 inline mr-0.5" />{unmappedCount} unmapped
                  </span>
                )}
                {unknownLabelCount > 0 && (
                  <span className="ml-2 text-amber-300/80" data-testid="field-mapping-unknown-count">
                    · {unknownLabelCount} unlabeled
                  </span>
                )}
              </span>
              <div className="ml-auto flex items-center gap-2">
                {unknownLabelCount > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={runSuggestLabels}
                    disabled={suggesting}
                    data-testid="field-mapping-suggest-labels-btn"
                    className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10 h-7 text-[11px]"
                    title="Use Claude AI to propose human-readable labels based on each field's ID, sample value, and canonical mapping"
                  >
                    {suggesting ? (
                      <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Asking Claude…</>
                    ) : (
                      <><Sparkles className="w-3 h-3 mr-1.5" /> Suggest labels (AI)</>
                    )}
                  </Button>
                )}
                {unknownLabelCount > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={runBackfill}
                    disabled={backfilling}
                    data-testid="field-mapping-backfill-btn"
                    className="border-sky-500/30 text-sky-300 hover:bg-sky-500/10 h-7 text-[11px]"
                    title="Walk every template (including inactive) and rewrite the label registry — recovers labels from templates that existed before the label registry feature shipped."
                  >
                    {backfilling ? (
                      <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Backfilling…</>
                    ) : (
                      <><Search className="w-3 h-3 mr-1.5" /> Backfill labels</>
                    )}
                  </Button>
                )}
                {unmappedCount > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={runAutoSuggest}
                    disabled={autoSuggesting}
                    data-testid="field-mapping-auto-suggest-btn"
                    className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 h-7 text-[11px]"
                    title="Scan unmapped fields and propose canonical columns based on field name + sample value"
                  >
                    {autoSuggesting ? (
                      <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Suggesting…</>
                    ) : (
                      <><Wand2 className="w-3 h-3 mr-1.5" /> Auto-suggest</>
                    )}
                  </Button>
                )}
                {dirtyCount > 0 && (
                  <span className="text-emerald-300" data-testid="field-mapping-dirty-count">
                    {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <Input
                placeholder="Search by field name or label…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="field-mapping-search"
                className="pl-8 bg-black/30 border-white/10 text-white text-sm h-9"
              />
            </div>

            {/* Field rows */}
            <div className="space-y-1.5 overflow-y-auto flex-1 pr-1">
              {filtered.length === 0 && (
                <div className="text-center text-gray-500 text-sm py-6">No fields match your search.</div>
              )}
              {filtered.map((field) => {
                const current = effectiveMapping(field);
                const source = effectiveSource(field);
                const badge = SOURCE_BADGE[source] || SOURCE_BADGE.none;
                const labelKnown = field.label && field.label !== field.key;
                const labelSrc = field.label_source || "none";
                const editing = field.key in labelDrafts;
                const isFlashing = flashKey === field.key;
                return (
                  <div
                    key={field.key}
                    ref={(el) => {
                      if (el) rowRefs.current[field.key] = el;
                      else delete rowRefs.current[field.key];
                    }}
                    data-testid={`field-mapping-row-${field.key}`}
                    className={`grid grid-cols-12 gap-2 items-center bg-white/[0.03] border rounded-lg p-2.5 hover:bg-white/[0.05] transition-all duration-500 ${
                      isFlashing
                        ? "border-emerald-400/70 shadow-[0_0_0_3px_rgba(16,185,129,0.18)] bg-emerald-500/[0.06]"
                        : "border-white/5"
                    }`}
                  >
                    <div className="col-span-7 min-w-0">
                      {/* Iter158 — for fields with NO resolved label, show
                          the inline input always-visible so the user can
                          type → Tab → next, no pencil click needed. Saves
                          on blur (when the value is non-empty). For known
                          labels, keep the read-only display + pencil. */}
                      {(!labelKnown || editing) ? (
                        <div className="flex items-center gap-1.5">
                          <Input
                            autoFocus={editing}
                            value={
                              editing
                                ? (labelDrafts[field.key] ?? "")
                                : (labelDrafts[field.key] ?? "")
                            }
                            onFocus={() => {
                              // First focus on an unknown row implicitly
                              // promotes it to "editing" so the existing
                              // save/cancel handlers work uniformly.
                              if (!editing) {
                                setLabelDrafts((p) => ({ ...p, [field.key]: "" }));
                              }
                            }}
                            onChange={(e) =>
                              setLabelDrafts((p) => ({ ...p, [field.key]: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                saveLabel(field);
                              }
                              if (e.key === "Escape") cancelEditLabel(field.key);
                            }}
                            onBlur={() => {
                              // Auto-save on blur when there's content —
                              // makes Tab-through-29-rows work with zero
                              // extra clicks. Empty drafts just clear.
                              const draft = (labelDrafts[field.key] ?? "").trim();
                              if (draft) {
                                saveLabel(field);
                              } else if (editing) {
                                cancelEditLabel(field.key);
                              }
                            }}
                            placeholder={
                              labelKnown
                                ? "Friendly label (e.g. Cold Outbound Calls)"
                                : "Type a friendly label and press Tab…"
                            }
                            data-testid={`field-mapping-label-input-${field.key}`}
                            className={`bg-black/30 border-white/10 text-white text-sm h-7 flex-1 ${
                              !labelKnown && !labelDrafts[field.key] ? "border-amber-500/30 placeholder:text-amber-300/40" : ""
                            }`}
                          />
                          {labelSaving === field.key && (
                            <Loader2 className="w-3 h-3 animate-spin text-emerald-400" />
                          )}
                          {editing && (
                            <button
                              type="button"
                              onClick={() => cancelEditLabel(field.key)}
                              aria-label="Cancel label edit"
                              data-testid={`field-mapping-label-cancel-${field.key}`}
                              className="text-gray-500 hover:text-white"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          {/* The ID chip still rendered below for context. */}
                          <button
                            type="button"
                            onClick={() => copyId(field.key)}
                            data-testid={`field-mapping-id-chip-${field.key}`}
                            className="font-mono text-[10px] bg-white/[0.04] hover:bg-white/[0.08] border border-white/5 rounded px-1.5 py-0.5 text-gray-400 hover:text-emerald-300 inline-flex items-center gap-1 transition-colors shrink-0"
                            title="Copy field ID"
                          >
                            {field.key}
                            <Copy className="w-2.5 h-2.5 opacity-60" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className={`text-sm font-medium truncate ${labelKnown ? "text-white" : "text-amber-300/90"}`}
                              title={field.label}
                              data-testid={`field-mapping-label-${field.key}`}
                            >
                              {labelKnown ? field.label : "Unknown label"}
                            </span>
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded border ${badge.color} shrink-0 uppercase tracking-wider`}
                            >
                              {badge.label}
                            </span>
                            <button
                              type="button"
                              onClick={() => startEditLabel(field)}
                              aria-label="Edit label"
                              data-testid={`field-mapping-label-edit-${field.key}`}
                              className="text-gray-500 hover:text-emerald-300 shrink-0"
                              title={labelKnown ? "Rename this field" : "Add a friendly label"}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => copyId(field.key)}
                              data-testid={`field-mapping-id-chip-${field.key}`}
                              className="font-mono bg-white/[0.04] hover:bg-white/[0.08] border border-white/5 rounded px-1.5 py-0.5 text-gray-400 hover:text-emerald-300 inline-flex items-center gap-1 transition-colors"
                              title="Copy field ID"
                            >
                              {field.key}
                              <Copy className="w-2.5 h-2.5 opacity-60" />
                            </button>
                            {labelSrc !== "none" && labelSrc !== "manual" && (
                              <span className="text-gray-600">
                                · label from {labelSrc === "template" ? "template" : "history"}
                              </span>
                            )}
                            {labelSrc === "manual" && (
                              <span className="text-emerald-400/70">· manual label</span>
                            )}
                            {field.sample_value != null && (
                              <span className="text-gray-600">· sample: {field.sample_value}</span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="col-span-1 text-center text-gray-500 text-xs">→</div>
                    <div className="col-span-4">
                      <Select value={current} onValueChange={(v) => setMapping(field.key, v)}>
                        <SelectTrigger
                          data-testid={`field-mapping-select-${field.key}`}
                          className="bg-black/30 border-white/10 text-white text-xs h-8"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10">
                          {CANONICAL_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} className="text-white text-sm">
                              {opt.value === current && (
                                <CheckCircle2 className="w-3 h-3 mr-1.5 inline text-emerald-400" />
                              )}
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer actions */}
            <div className="flex justify-end gap-2 pt-3 border-t border-white/5">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="bg-transparent border-white/10 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={saving || dirtyCount === 0}
                data-testid="field-mapping-save"
                className="bg-gradient-to-r from-emerald-700 to-emerald-500 text-white disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save {dirtyCount > 0 && `(${dirtyCount})`}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
